import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const PULL_THRESHOLD = 70;
const MAX_PULL = 110;
const RESISTANCE = 0.5;
const MIN_VISIBLE_MS = 500;

const PullToRefresh = ({ onRefresh, disabled = false, isLightTheme, children }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef(null);
  const touchStateRef = useRef(null);
  const disabledRef = useRef(disabled);
  const refreshingRef = useRef(refreshing);
  const onRefreshRef = useRef(onRefresh);
  const pullDistanceRef = useRef(0);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);
  useEffect(() => {
    refreshingRef.current = refreshing;
  }, [refreshing]);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);
  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  // Listener nativo (no pasivo) requerido para poder llamar preventDefault
  // en touchmove: React adjunta los handlers sintéticos como pasivos.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const resetPull = () => {
      touchStateRef.current = null;
      setIsPulling(false);
      setPullDistance(0);
    };

    const onTouchStart = (e) => {
      if (
        disabledRef.current ||
        refreshingRef.current ||
        e.touches.length !== 1 ||
        window.scrollY > 2
      ) {
        return;
      }
      const touch = e.touches[0];
      touchStateRef.current = { startX: touch.clientX, startY: touch.clientY, pulling: false };
    };

    const onTouchMove = (e) => {
      const state = touchStateRef.current;
      if (!state || disabledRef.current || refreshingRef.current) return;

      const touch = e.touches[0];
      const dy = touch.clientY - state.startY;
      const dx = touch.clientX - state.startX;

      if (!state.pulling) {
        if (dy <= 8 || Math.abs(dx) > Math.abs(dy)) return;
        if (window.scrollY > 2) {
          touchStateRef.current = null;
          return;
        }
        state.pulling = true;
        setIsPulling(true);
      }

      if (window.scrollY > 2) {
        resetPull();
        return;
      }

      e.preventDefault();
      setPullDistance(Math.min(MAX_PULL, dy * RESISTANCE));
    };

    const onTouchEnd = () => {
      const state = touchStateRef.current;
      touchStateRef.current = null;
      setIsPulling(false);

      if (!state?.pulling) {
        setPullDistance(0);
        return;
      }

      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        setPullDistance(PULL_THRESHOLD);
        setRefreshing(true);
        const start = Date.now();
        Promise.resolve(onRefreshRef.current?.())
          .catch(() => {})
          .finally(() => {
            const elapsed = Date.now() - start;
            const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
            setTimeout(() => {
              setRefreshing(false);
              setPullDistance(0);
            }, wait);
          });
      } else {
        setPullDistance(0);
      }
    };

    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd, { passive: true });
    node.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  const progress = Math.min(1, pullDistance / PULL_THRESHOLD);
  const indicatorHeight = refreshing ? 56 : pullDistance;
  const showIndicator = indicatorHeight > 0;

  return (
    <Box ref={containerRef} sx={{ overscrollBehaviorY: "contain" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-end",
          height: indicatorHeight,
          transition: isPulling ? "none" : "height 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
          overflow: "hidden",
        }}
      >
        {showIndicator && (
          <Box
            sx={{
              width: 38,
              height: 38,
              mb: 1,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              background: isLightTheme
                ? "rgba(255,255,255,0.92)"
                : "rgba(18,18,18,0.9)",
              boxShadow: refreshing
                ? "0 0 0 1px rgba(0,212,255,0.45), 0 8px 22px rgba(0,212,255,0.35)"
                : "0 0 0 1px rgba(0,212,255,0.25), 0 6px 16px rgba(0,212,255,0.18)",
              backdropFilter: "blur(12px)",
              transition: "box-shadow 0.2s ease",
            }}
          >
            {refreshing ? (
              <CircularProgress size={19} thickness={5} sx={{ color: "#00d4ff" }} />
            ) : (
              <RefreshIcon
                sx={{
                  fontSize: 19,
                  color: "#00d4ff",
                  opacity: 0.45 + progress * 0.55,
                  transform: `rotate(${progress * 340}deg) scale(${0.75 + progress * 0.25})`,
                  transition: "opacity 0.05s linear",
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {children}
    </Box>
  );
};

export default PullToRefresh;
